import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { 
  Target,
  TrendingUp,
  Shield,
  Bell
} from 'lucide-react'
import { SmartBudgetAssistant } from '../assistant/SmartBudgetAssistant'

interface BudgetIntroStepProps {
  onNext: () => void
  onSkip: () => void
}

export function BudgetIntroStep({ onNext, onSkip }: BudgetIntroStepProps) {
  const { t } = useTranslation()
  const [showSmartAssistant, setShowSmartAssistant] = useState(false)

  const benefits = [
    {
      icon: <Target className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.budgetIntro.benefits.control', 'Take Control'),
      description: t('onboarding.budgetIntro.benefits.controlDesc', 'Set spending limits for different categories to stay on track')
    },
    {
      icon: <TrendingUp className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.budgetIntro.benefits.insights', 'Get Insights'),
      description: t('onboarding.budgetIntro.benefits.insightsDesc', 'See where your money goes and identify areas for improvement')
    },
    {
      icon: <Shield className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.budgetIntro.benefits.protection', 'Avoid Overspending'),
      description: t('onboarding.budgetIntro.benefits.protectionDesc', 'Get alerts before you exceed your spending limits')
    },
    {
      icon: <Bell className="h-6 w-6 text-[#439476]" />,
      title: t('onboarding.budgetIntro.benefits.alerts', 'Smart Alerts'),
      description: t('onboarding.budgetIntro.benefits.alertsDesc', 'Receive notifications to keep your spending in check')
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="relative mx-auto w-16 h-16 mb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-[#439476] to-[#387565] rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-[#439476] to-[#387565] rounded-xl p-3 shadow-lg">
            <Target className="h-10 w-10 text-white" />
          </div>
        </div>
        
        <h3 className="text-xl font-bold bg-gradient-to-r from-[#1C1C1C] to-[#383838] dark:from-[#E8E8E8] dark:to-[#CCCCCC] bg-clip-text text-transparent">
          {t('onboarding.budgetIntro.title', 'Take Control with Smart Budgeting')}
        </h3>
        <p className="text-muted-foreground text-lg">
          {t('onboarding.budgetIntro.description', 'Budgets help you spend intentionally and reach your financial goals faster.')}
        </p>
      </div>

      {/* Hero Illustration */}
      <Card className="p-8 bg-gradient-to-r from-[#439476]/5 to-[#387565]/5 border-[#439476]/20">
        <div className="text-center space-y-4">
          <div className="flex justify-center items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">{t('onboarding.budgetIntro.example.safe', 'Safe Zone')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
              <span className="text-sm font-medium">{t('onboarding.budgetIntro.example.warning', 'Warning')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium">{t('onboarding.budgetIntro.example.danger', 'Over Budget')}</span>
            </div>
          </div>
          
          <div className="bg-white dark:bg-[#182438] rounded-lg p-4 shadow-sm">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">{t('onboarding.budgetIntro.example.groceries', 'Groceries')}</span>
                <span className="text-sm font-medium">$320 / $400</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '80%' }}></div>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground">
            {t('onboarding.budgetIntro.example.description', 'Visual progress bars help you track spending in real-time')}
          </p>
        </div>
      </Card>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {benefits.map((benefit, index) => (
          <Card key={index} className="p-4 bg-white/80 dark:bg-[#182438]/80">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[#439476]/10">
                {benefit.icon}
              </div>
              <div className="space-y-1">
                <h4 className="font-medium text-sm">{benefit.title}</h4>
                <p className="text-xs text-muted-foreground">{benefit.description}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Smart Assistant CTA */}
      <Card className="p-6 text-center space-y-6 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200 dark:border-emerald-800">
        <div className="relative mx-auto w-12 h-12 mb-4">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl p-2 shadow-lg">
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 01 7.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 01-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
        </div>
        <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">
          {t('onboarding.budgetIntro.smartAssistant.title', 'Not sure where to start?')}
        </h4>
        <p className="text-emerald-700 dark:text-emerald-300 text-sm">
          {t('onboarding.budgetIntro.smartAssistant.description', 'Our Smart Budget Assistant will help you create personalized budgets based on your income and expenses in just a few minutes.')}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            onClick={onSkip}
            className="text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-900/20"
          >
            {t('onboarding.budgetIntro.skipBudgets', 'Skip Budgets')}
          </Button>
          <Button
            onClick={() => setShowSmartAssistant(true)}
            className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white px-6 shadow-lg"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 01 7.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 01-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {t('onboarding.budgetIntro.smartAssistant.cta', 'Try Smart Assistant')}
          </Button>
          <Button
            variant="outline"
            onClick={onNext}
            className="text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            {t('onboarding.budgetIntro.manualSetup', 'Manual Setup')}
          </Button>
        </div>
      </Card>

      {/* Smart Budget Assistant Modal */}
      <SmartBudgetAssistant
        isOpen={showSmartAssistant}
        onClose={() => setShowSmartAssistant(false)}
        onComplete={() => {
          setShowSmartAssistant(false)
          onNext() // Continue to next onboarding step
        }}
      />
    </div>
  )
} 